/**
 * Base maps and the two extra layers, all from PDOK as plain raster tiles. No
 * key, no tile server of our own, and quiet enough that the balloons stay the
 * loudest thing on screen.
 *
 * De kadastrale kaart en de luchtfoto staan beide in WebMercator
 * (TILEMATRIXSET=EPSG:3857), dus ze gaan rechtstreeks in MapLibre zonder
 * projectietrucs. Nagemeten op z14-z20 boven Amsterdam: 200, image/png
 * respectievelijk image/jpeg.
 */
import type { StyleSpecification } from 'maplibre-gl';
import type { Kaartstijl } from '@/lib/instellingen';
import { BRON_PASSANTEN, PASSANTEN_BRON, PASSANTEN_LAAG } from './passanten';

export const KAARTSTIJL_LABELS: Record<Kaartstijl, string> = {
  grijs: 'Grijs',
  standaard: 'Standaard',
  pastel: 'Pastel',
  luchtfoto: 'Luchtfoto',
};

export const ATTRIBUTIE = '&copy; <a href="https://www.kadaster.nl">Kadaster</a> / PDOK';

const BRT = 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0';
const LUCHTFOTO = 'https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0';
/** LAYER=Kadastralekaart, TILEMATRIXSET=EPSG:3857, FORMAT=image/png. */
const KADASTER = 'https://service.pdok.nl/kadaster/kadastralekaart/wmts/v5_0';

export const LUCHTFOTO_TEGELS = `${LUCHTFOTO}/Actueel_ortho25/EPSG:3857/{z}/{x}/{y}.jpeg`;
export const PERCELEN_TEGELS = `${KADASTER}/Kadastralekaart/EPSG:3857/{z}/{x}/{y}.png`;

/**
 * De kadastrale kaart tekent pas vanaf z17; daaronder komt er een lege tegel van
 * 854 byte terug. Onder die grens vragen we hem niet op, en het paneel zegt het.
 *
 * Deze grens is de zoom waarop de laag zijn eerste tegel ophaalt, niet de zoom
 * waar hij nog leeg is: stond hier 16, dan haalde de app op z16 een tegelrij van
 * 854 byte op die niets tekent.
 */
export const PERCELEN_MIN_ZOOM = 17;

export const BRON_ONDERGROND = 'ondergrond-tegels';
export const BRON_LUCHTFOTO = 'luchtfoto-tegels';
export const BRON_PERCELEN = 'percelen-tegels';

export const LAAG_ONDERGROND = 'ondergrond-raster';
export const LAAG_LUCHTFOTO = 'luchtfoto-raster';
export const LAAG_PERCELEN = 'percelen-raster';

/**
 * Gedempte dekking: beide lagen liggen ónder de woningpunten en mogen de
 * ballonnen niet overstemmen.
 */
export const DEKKING_LUCHTFOTO = 0.55;
export const DEKKING_PERCELEN = 0.5;

export function kaartstijl(stijl: Kaartstijl): StyleSpecification {
  const luchtfotoAlsBasis = stijl === 'luchtfoto';
  return {
    version: 8,
    sources: {
      [BRON_ONDERGROND]: {
        type: 'raster',
        tiles: [luchtfotoAlsBasis ? LUCHTFOTO_TEGELS : `${BRT}/${stijl}/EPSG:3857/{z}/{x}/{y}.png`],
        tileSize: 256,
        minzoom: 0,
        maxzoom: luchtfotoAlsBasis ? 20 : 19,
        attribution: ATTRIBUTIE,
      },
      [BRON_LUCHTFOTO]: {
        type: 'raster',
        tiles: [LUCHTFOTO_TEGELS],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 20,
        attribution: ATTRIBUTIE,
      },
      [BRON_PERCELEN]: {
        type: 'raster',
        tiles: [PERCELEN_TEGELS],
        tileSize: 256,
        minzoom: PERCELEN_MIN_ZOOM,
        maxzoom: 20,
        attribution: ATTRIBUTIE,
      },
      [BRON_PASSANTEN]: PASSANTEN_BRON,
    },
    layers: [
      { id: 'achtergrond', type: 'background', paint: { 'background-color': '#f4f2ed' } },
      { id: LAAG_ONDERGROND, type: 'raster', source: BRON_ONDERGROND },
      {
        id: LAAG_LUCHTFOTO,
        type: 'raster',
        source: BRON_LUCHTFOTO,
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': DEKKING_LUCHTFOTO },
      },
      {
        id: LAAG_PERCELEN,
        type: 'raster',
        source: BRON_PERCELEN,
        minzoom: PERCELEN_MIN_ZOOM,
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': DEKKING_PERCELEN },
      },
      PASSANTEN_LAAG,
    ],
  };
}

/** Amsterdam; the only city in the current data. */
export const START_CENTRUM: [number, number] = [4.8952, 52.3702];
export const START_ZOOM = 11.6;
